import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';;
import { ContractItem } from '@core/models/contract-item.model';
import { ContractThemaNego } from '@core/models/contract-thema-nego.model';
import { Contract } from '@core/models/contract.model';
import { TvChannel } from '@core/models/tv-channel.model';
import { ContractCgvModelUpdateService } from '@core/services/contract-cgv-model-update/contract-cgv-model-update.service';
import { ContractThemaNegoService } from '@core/services/contract-thema-nego/contract-thema-nego.service';
import { TvChannelService } from '@core/services/tv-channel/tv-channel.service';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
standalone: false,
  selector: 'app-thema-not-guaranteed-managing-dialog',
  templateUrl: './thema-not-guaranteed-managing-dialog.component.html',
  styleUrls: ['./thema-not-guaranteed-managing-dialog.component.scss'],
})
export class ThemaNotGuaranteedManagingDialogComponent implements OnInit {
  public firstDayOfYear = moment().startOf('year');
  public lastDayOfYear = moment().endOf('year');

  public isChannelListLoading: boolean = false;
  public isSaving: boolean = false;
  public isEditing: boolean = false;

  public negotiationDateForm: FormGroup;
  public budgetFormControl = new FormControl(null);
  public nctRateFormControl = new FormControl(null, [Validators.required, Validators.min(-999), Validators.max(0)]);
  public searchChannelFormControl = new FormControl(null, [Validators.required]);

  public editStartDate = '';
  public editEndDate = '';
  public advertiserId = null;
  public contractYear = null;

  // public selectedChannels: Set<any> = new Set();
  public selectedChannel: TvChannel;
  public selectAllChannels = false;

  public channels: TvChannel[];
  public channels$: Observable<TvChannel[]>;
  public filteredChannelList$: Observable<TvChannel[]>;

  componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialog,
    public snackBar: MatSnackBar,
    public contractThemaNegoService: ContractThemaNegoService,
    private tvChannelService: TvChannelService,
    private contractCgvModelUpdateService: ContractCgvModelUpdateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract: Contract;
      contractItem: ContractItem;
      themaNegotiationData: ContractThemaNego;
      isDuplication: boolean;
      isGuarantee: string;
    }
  ) {
    this.componentDestroyed$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.initForm();
    this.initData();

    this.getChannels();

    this.handleSelectedChannelsChange();
  }

  // INIT DIALOG
  public initDialog() {
    if (this.data.themaNegotiationData) {
      // Dates
      if (this.data.themaNegotiationData.startDate && this.data.themaNegotiationData.endDate) {
        this.negotiationDateForm?.get('startDate').setValue(this.data.themaNegotiationData.startDate);
        this.negotiationDateForm?.get('endDate').setValue(this.data.themaNegotiationData.endDate);
      }

      // Nct Rate
      if (this.data.themaNegotiationData.nctRate) {
        this.nctRateFormControl?.setValue(this.data.themaNegotiationData.nctRate);
      }

      // Value
      if (this.data.themaNegotiationData.value) {
        this.budgetFormControl?.setValue(this.data.themaNegotiationData.value);
      }

      // Channels Conditions
      if (this.data.themaNegotiationData.channelConditions) {
        this.selectedChannel = null;

        this.data.themaNegotiationData.channelConditions.map(channelCondition => {
          this.selectedChannel = channelCondition.channel;
        });
      }
    }
  }

  // INITFORM

  public initForm() {
    this.firstDayOfYear = moment().year(Number(this.data.contractItem?.year)).startOf('year');
    this.lastDayOfYear = moment().year(Number(this.data.contractItem?.year)).endOf('year');

    this.negotiationDateForm = this.fb.group({
      startDate: [this.firstDayOfYear || null, [Validators.required]],
      endDate: [this.lastDayOfYear || null, [Validators.required]],
    });

    this.budgetFormControl.setValidators([Validators.required, Validators.min(0)]);
  }

  // INIT DATA
  public initData() {
    this.isChannelListLoading = true;
    if (this.data) {
      this.advertiserId = this.data.contract?.advertiserId;
      this.contractYear = this.data.contract?.stateContractYear;

      this.isEditing = !(this.data.themaNegotiationData === null);
    }
  }

  public getChannels(): void {
    this.tvChannelService.getThemaChannels().subscribe(
      (channels: TvChannel[]) => {
        this.isChannelListLoading = false;
        this.channels = channels;
        this.filteredChannelList$ = this.channels$;
        this.initFilteredChannelList();
        this.initDialog();
      },
      error => {
        this.isChannelListLoading = false;
        const actualError = typeof error === 'function' ? error() : error;
        console.error('Error loading channels', actualError);
      }
    );
  }

  // OTHER
  public displaySnackBar(message: string, panelClass: string) {
    this.snackBar.open(message, null, {
      duration: 4000,
      verticalPosition: 'top',
      panelClass: [panelClass],
    });
  }

  public displayChannel(channel) {
    if (channel) {
      return channel?.name && channel?.id ? `${channel?.name}` : channel;
    }
  }

  public _getChannelLabel(value: string | TvChannel): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  public _filterChannel(value: string = ''): TvChannel[] {
    const filterValue = value.toLowerCase();

    return this.channels.filter(channel => {
      return channel.id.toString() == filterValue || channel.name.toLowerCase().includes(filterValue);
    });
  }

  public initFilteredChannelList() {
    if (!this.isChannelListLoading) {
      this.filteredChannelList$ = this.searchChannelFormControl.valueChanges.pipe(
        takeUntil(this.componentDestroyed$),
        startWith(''),
        map(value => {
          return this._getChannelLabel(value);
        }),
        map(label => {
          return label ? this._filterChannel(label) : this.channels.slice();
        })
      );
    }
  }

  public handleSelectedChannelsChange() {
    this.searchChannelFormControl.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe(value => {
      if (typeof value !== 'string') {
        this.selectedChannel = this.channels.find(channel => channel.id === value.id || channel.name === value.name);
      }
    });
  }

  public removeChannelItem(itemToRemove: TvChannel) {
    this.selectedChannel = null;
  }

  // MANAGE ACTION BUTTON

  getNegotiationDate() {
    let formattedStartDate = '';
    let formattedEndDate = '';

    const startDateValue = this.negotiationDateForm.get('startDate')?.value;
    const endDateValue = this.negotiationDateForm.get('endDate')?.value;

    if (startDateValue && typeof startDateValue !== 'string' && endDateValue && typeof endDateValue !== 'string') {
      formattedStartDate = moment(startDateValue).format('YYYY-MM-DD');
      formattedEndDate = moment(endDateValue).format('YYYY-MM-DD');
    } else if (startDateValue && typeof startDateValue === 'string' && endDateValue && typeof endDateValue === 'string') {
      let [day, month, year] = this.negotiationDateForm.get('startDate').value.split('/');
      formattedStartDate = `${year}-${month}-${day}`;
      [day, month, year] = this.negotiationDateForm.get('endDate').value.split('/');
      formattedEndDate = `${year}-${month}-${day}`;
    }

    return {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };
  }

  public getValueToSave() {
    if (
      this.negotiationDateForm?.valid &&
      this.budgetFormControl?.valid &&
      this.nctRateFormControl?.valid &&
      this.selectedChannel !== undefined
    ) {
      this.isSaving = true;

      // Dates
      const negotiationDate = this.getNegotiationDate();

      return {
        id: !this.data.isDuplication ? (this.data?.themaNegotiationData?.id ? this.data.themaNegotiationData.id : null) : null,
        contractItemId: this.data.contractItem.id,
        clientId: this.data.contract.id,
        isGuarantee: false,
        startDate: negotiationDate.startDate || '',
        endDate: negotiationDate.endDate || '',
        value: this.budgetFormControl.value,
        nctRate: this.nctRateFormControl.value,
        stateContractYear: this.data.contract.stateContractYear,
        channelConditions: [this.selectedChannel],
      };
    } else {
      this.markAllAsTouched();
      this.displaySnackBar('Veuillez remplir ou corriger le formulaire.', 'tv-chip-error');
    }
  }

  markAllAsTouched() {
    this.negotiationDateForm.markAllAsTouched();
    this.budgetFormControl.markAsTouched();
    this.nctRateFormControl.markAsTouched();
    this.searchChannelFormControl.markAllAsTouched();
  }

  // Save
  onValidationButtonClick() {
    const data = this.getValueToSave();

    if (data) {
      if (data.id) {
        // UDPATE
        this.update(data);
      } else {
        // CREATE
        this.create(data);
      }
    }
  }

  create(data) {
    this.contractThemaNegoService.create(data).subscribe(
      rs => {
        this.updateContractCgvModel();
      },
      error => {
        this.isSaving = false;
      }
    );
  }

  update(data) {
    this.contractThemaNegoService.edit(data).subscribe(
      rs => {
        this.updateContractCgvModel();
      },
      error => {
        this.isSaving = false;
      }
    );
  }

  public updateContractCgvModel() {
    this.contractCgvModelUpdateService.updateContractCgvModel(this.data.contractItem.id, this.data.contractItem).subscribe(
      response => {
        this.isSaving = false;
        this.displaySnackBar('Enregistrement terminé.', 'tv-chip-success');
        this.dialogRef.closeAll();
      },
      error => {
        this.displaySnackBar("Erreur lors de la sauvegarde dans l'ancien modèle. Veuillez contacter le support.", 'tv-chip-error');
        this.isSaving = false;
      }
    );
  }

  // Cancel
  public onCancelUpdateButtonClick() {
    // this.isShowOption = false;
    this.displaySnackBar("L'ajout / modification a été annulée.", 'tv-chip-error');
    this.dialogRef.closeAll();
  }

  enforceNegativeNctRate(): void {
    const value = this.nctRateFormControl.value;
    if (value != null && !isNaN(value) && value > 0) {
      this.nctRateFormControl.setValue(-Math.abs(value));
    }
  }
}
